import type { ReactNode } from 'react';
import { Loader2, AlertTriangle, ShieldOff, CheckCircle2, SearchX, type LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * Estados de carga, error y sin permiso de una pantalla de datos. Centrados en el
 * alto del <main>, sin overflow.
 *
 * Vivian dentro de `admin/portal-cobranza` y los importaban otros portales desde
 * ahi; son UI sin dominio, asi que su lugar es `components/ui`. Los nombres
 * mantienen el prefijo `Collection` para no romper los menus que ya los usan.
 */

const WRAP = 'flex flex-col items-center justify-center gap-3 text-center min-h-[calc(100vh-160px)]';

export function CollectionLoading({ label = 'Cargando...' }: { label?: string }) {
  return (
    <div className={WRAP}>
      <Loader2 className="w-8 h-8 animate-spin text-success" />
      <span className="text-base font-semibold text-foreground">{label}</span>
    </div>
  );
}

/**
 * Carga DENTRO de un modal, panel o pestana, donde no hay alto de pantalla que
 * ocupar. Mismo spinner verde que `CollectionLoading` para que la espera se vea
 * igual en toda la app; el alto se pasa cuando se quiere reservar espacio y
 * evitar que el contenido salte al llegar.
 */
export function InlineLoading({
  label = 'Cargando...',
  className,
}: {
  label?: string;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col items-center justify-center gap-2.5 py-12 text-center', className)}>
      <Loader2 className="size-6 animate-spin text-success" />
      <span className="text-[13px] font-medium text-muted-foreground">{label}</span>
    </div>
  );
}

export function CollectionError({
  title = 'No pudimos cargar la información',
  onRetry,
}: {
  title?: string;
  onRetry?: () => void;
}) {
  return (
    <div className={WRAP}>
      <div className="flex size-14 items-center justify-center rounded-full bg-danger/10">
        <AlertTriangle className="size-7 text-danger" />
      </div>
      <div>
        <p className="text-base font-semibold text-foreground">{title}</p>
        <p className="text-sm text-muted-foreground mt-1.5 max-w-md">
          Hubo un problema al obtener la información. Revisa tu conexión e inténtalo de nuevo.
        </p>
      </div>
      {onRetry && (
        <Button
          variant="outline"
          size="sm"
          onClick={onRetry}
          className="h-9 text-[13px] mt-1 hover:border-success hover:text-success hover:bg-success/5"
        >
          Reintentar
        </Button>
      )}
    </div>
  );
}

/**
 * Las RPC del portal validan permisos por dentro y responden 403 (ERRCODE 42501).
 * Cuando eso pasa se muestra esto, no una tabla vacía ni un error de carga: la diferencia
 * entre "no tienes acceso" y "falló la consulta" importa para no mandar a la gente a
 * reintentar algo que nunca va a funcionar.
 */
export function CollectionSinPermiso({
  title = 'No tienes permiso para ver esta información',
}: { title?: string }) {
  return (
    <div className={WRAP}>
      <div className="flex size-14 items-center justify-center rounded-full bg-muted">
        <ShieldOff className="size-7 text-muted-foreground" />
      </div>
      <div>
        <p className="text-base font-semibold text-foreground">{title}</p>
        <p className="text-sm text-muted-foreground mt-1.5 max-w-md">
          Tu rol no tiene acceso de lectura a este módulo. Si lo necesitas, pídelo al
          administrador del sistema.
        </p>
      </div>
    </div>
  );
}

/**
 * Lista o bandeja sin filas, DENTRO del flujo de la pantalla (no ocupa el alto del <main>).
 *
 * Dos casos que se leen distinto y no deben verse igual:
 *   - `tono="ok"`: no hay nada pendiente. Es una buena noticia (palomita verde).
 *   - `tono="filtros"` (default): los filtros no dejaron pasar nada. Se ofrece limpiarlos con
 *     `accion`, para que el usuario no crea que la bandeja esta vacia de verdad.
 */
export function EmptyState({
  titulo,
  texto,
  nota,
  tono = 'filtros',
  accion,
  className,
  icono,
  compacto = false,
  children,
}: {
  titulo: string;
  texto?: string;
  /** Aclaracion secundaria (alcance de la consulta, truncado). */
  nota?: string;
  tono?: 'ok' | 'filtros';
  accion?: { label: string; onClick: () => void };
  className?: string;
  /** Ícono propio (por defecto, el del tono). */
  icono?: LucideIcon;
  /** Sin borde y con menos aire: para vacíos dentro de un panel angosto (menú lateral). */
  compacto?: boolean;
  /** Contenido propio bajo el texto (un CTA que no es un botón simple, una nota con enlace). */
  children?: ReactNode;
}) {
  const Icono = icono ?? (tono === 'ok' ? CheckCircle2 : SearchX);
  return (
    // Sin recuadro, como el SEmptyState de la app de clientes (Eduardo, 2026-10-30: "es molesto esa
    // línea de una card"): ícono en círculo teñido de 56 px, título y mensaje centrados.
    <div className={cn(compacto ? 'px-3 py-8 text-center' : 'px-6 py-14 text-center', className)}>
      <div
        className={cn(
          'mx-auto mb-4 flex items-center justify-center rounded-full',
          compacto ? 'size-12' : 'size-14',
          tono === 'ok' ? 'bg-success/10' : 'bg-primary/10',
        )}
      >
        <Icono className={cn(compacto ? 'size-6' : 'size-7', tono === 'ok' ? 'text-success' : 'text-primary')} aria-hidden />
      </div>
      <p className="text-base font-semibold text-foreground">{titulo}</p>
      {texto && <p className="mx-auto mt-1.5 max-w-[420px] text-[13px] text-muted-foreground">{texto}</p>}
      {nota && <p className="mx-auto mt-2 max-w-md text-[11.5px] text-muted-foreground/80">{nota}</p>}
      {accion && (
        <Button variant="outline" size="sm" onClick={accion.onClick} className="mt-4 h-9 text-[13px]">
          {accion.label}
        </Button>
      )}
      {children && <div className="mt-4 flex justify-center">{children}</div>}
    </div>
  );
}
